import React from 'react';
import styled from 'styled-components';
import { Radio, Alert, Spin } from 'antd';
import { ReactComponent as MpesaIcon } from '../../assets/images/Mpesa.svg';
import { ReactComponent as MastercardIcon } from '../../assets/images/Mastercard.svg';
import { ReactComponent as VisaIcon } from '../../assets/images/Visa.svg';
import { Card } from '@solar-icons/react';
import{ ReactComponent as PaystackLogo} from '../../assets/images/paystack.svg';  

const StyledPaystackLogo = styled(PaystackLogo)`
  && {
    path {
      fill: #00C3F7 !important;
    }
  }
`;

import { BLACK } from '../../utils/colors';
import PhoneNumberInput from '../PhoneNumberInput';
import { StyledParagraph } from '../CustomAntdComponents/Typography';

const SelectorContainer = styled.div``;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${BLACK};
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
  }
`;

const StyledRadioGroup = styled(Radio.Group)`
  width: 100%;
  margin-bottom: 24px;

  .ant-radio {
    top: 0;
  }

  .ant-radio-inner {
    width: 24px;
    height: 24px;
    border-color: #D1D5DB;
    border-width: 1.5px;
  }
  
  .ant-radio-checked .ant-radio-inner {
    border-color: #FF9800; /* Matching the Figma orange */
    border-width: 7px;
    background-color: #FFFFFF;
  }

  .ant-radio-checked .ant-radio-inner::after {
    display: none;
  }

  .ant-radio-wrapper:hover .ant-radio-inner,
  .ant-radio:hover .ant-radio-inner {
    border-color: #FF9800;
  }
`;

const OptionContainer = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 20px 0;
  border-bottom: 2px solid #F3F4F6;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const LeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const OptionLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${BLACK};
`;

const IconGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NestedContent = styled.div`
   
  

  @media (max-width: 768px) {
    padding-left: 0;
  }
`;

type Props = {
  paymentMethods?: any[];
  selectedMethodId?: string | null;
  onMethodChange: (methodId: string) => void;
  phone?: string;
  onChangePhone?: (phone: string) => void;
  phoneError?: boolean;
  phoneErrorText?: string;
  loading?: boolean;
};

const renderMethodIcon = (name: string) => {
  const normalized = (name || '').toLowerCase();
  if (normalized.includes('mpesa')) {
    return <MpesaIcon width={24} height={24} />;
  }
  if (normalized.includes('paystack')) {
    return <StyledPaystackLogo width={24} height={24} />;
  }
  return <Card width={24} height={24} style={{ color: '#FF9800' }} />;
};

const PaymentMethodSelector = ({
  paymentMethods = [],
  selectedMethodId,
  onMethodChange,
  phone = '',
  onChangePhone,
  phoneError,
  phoneErrorText,
  loading = false
}: Props) => {
  if (loading) {
    return (
      <SelectorContainer>
        <SectionTitle>Select payment method</SectionTitle>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px 0' }}>
          <Spin size="default" tip="Loading payment methods..." />
        </div>
      </SelectorContainer>
    );
  }

  if (paymentMethods.length === 0) {
    return (
      <SelectorContainer>
        <SectionTitle>Select payment method</SectionTitle>
        <Alert
          message="Payment Methods Unavailable"
          description="No payment methods configured for this merchant. Please contact support or try again later."
          type="warning"
          showIcon
          style={{ borderRadius: 8 }}
        />
      </SelectorContainer>
    );
  }

  return (
    <SelectorContainer>
      <SectionTitle>Select payment method</SectionTitle>

      <StyledRadioGroup value={selectedMethodId} onChange={(e) => onMethodChange(e.target.value)}>
        {paymentMethods.map((method) => {
          const isSelected = selectedMethodId === method.Id;
          return (
            <OptionContainer active={isSelected} onClick={() => onMethodChange(method.Id)} key={method.Id}>
              <OptionHeader>
                <LeftSide>
                  {renderMethodIcon(method.PaymentModeName || method.Name)}
                  <OptionLabel>{method.Name || method.PaymentModeName}</OptionLabel>
                </LeftSide>
                <Radio value={method.Id} />
              </OptionHeader>

              {isSelected && method.IsMobile && (
                <NestedContent onClick={(e) => e.stopPropagation()}>
                  <div style={{ marginTop: 8 }}>
                    <StyledParagraph style={{ marginBottom: 8, fontSize: "14px", fontWeight: 500 }}>
                      Phone number
                    </StyledParagraph>
                    <PhoneNumberInput
                      name="contactPhone"
                      placeholder=""
                      onChange={(val) => onChangePhone?.(val)}
                      value={phone}
                      phoneOnly={true}
                      hasError={phoneError}
                      helperText={phoneErrorText}
                    />
                    <StyledParagraph style={{ marginTop: 8, fontSize: "12px", color: "#667085" }}>
                      You will get a PIN prompt on your device
                    </StyledParagraph>
                  </div>
                </NestedContent>
              )}
            </OptionContainer>
          );
        })}
      </StyledRadioGroup>
    </SelectorContainer>
  );
};

export default PaymentMethodSelector;
